import React from "react";
import PropTypes from "prop-types";
import { Picker } from "emoji-mart";
import { Flex, IconButton, Input, OutsideClick } from "@evertrue/et-components";
import { useChatCurrentUser } from "../contexts/chat-current-user-context";
import { useChatDependencyContext } from "../contexts/chat-dependency-context";
import { getETUserIDFromChatKitUser } from "../utils/utils";
import { useETCachedUserById } from "../contexts/et-user-context";
// this is so that if a user navigates away from a room and don't send their
// message, it is preserved
const saved_text = {};
const emoji_sprite_sheet_url = Picker.defaultProps.backgroundImageFn("apple", 64);

const useIsLoggedInUser = () => {
  const { currentChatUser } = useChatCurrentUser();
  const { oid } = useChatDependencyContext();
  const et_user_id = getETUserIDFromChatKitUser(oid, currentChatUser.id);
  const { user = {} } = useETCachedUserById(et_user_id);
  // this user comes in different format than Decorator expects
  const affiliations = user.roles ? user.roles.filter(i => i.organization_id === oid) : [];
  return { user, is_affiliated: Boolean(affiliations.length) };
};

const MessageTextArea = ({ roomName, sendMessage, roomId, ...props }) => {
  const [text, setText] = React.useState(saved_text[roomId] || "");
  const [emojiVisible, setEmojiVisible] = React.useState(false);
  const { currentChatUser } = useChatCurrentUser();
  const { is_affiliated } = useIsLoggedInUser();
  const input_ref = React.useRef();

  const setAndSaveText = str => {
    setText(str);
    saved_text[roomId] = str;
  };

  return (
    <Flex className="message-text-area">
      {/* use this to preload the sprite sheet so emojis load right away*/}
      <img src={emoji_sprite_sheet_url} style={{ display: "none" }} alt="emoji sprite sheet hidden" />
      {is_affiliated ? (
        <>
          <Input
            ref={input_ref}
            focusOnMount={true}
            type="text"
            className="message-text-area--input"
            placeholder={roomName ? `Message "${roomName}"` : "Type message..."}
            value={text}
            onChange={value => {
              currentChatUser.isTypingIn({ roomId });
              setAndSaveText(value);
            }}
            onKeyPress={e => {
              if (e.key === "Enter" && e.target.value.trim() !== "") {
                sendMessage({
                  text: e.target.value,
                  roomId,
                });
                setAndSaveText("");
              }
            }}
          />
          <IconButton
            title="emoji picker"
            icon="emoji"
            onClick={() => setEmojiVisible(state => !state)}
            className="message-text-area--emoji-button"
          />
        </>
      ) : (
        <div className="message-text-area--input">
          Sending messages disabled.
          <br /> Your user is not affiliated with this organization.
        </div>
      )}

      {emojiVisible && (
        <OutsideClick onOutsideClick={() => setEmojiVisible(false)}>
          <Picker
            // styles need to be inline here
            style={{ position: "absolute", right: 0, bottom: 0 }}
            onSelect={emoji => {
              setAndSaveText(text + emoji.native);
              setEmojiVisible(false);
              input_ref.current.focus();
            }}
            title=""
          />
        </OutsideClick>
      )}
    </Flex>
  );
};

MessageTextArea.propTypes = {
  roomName: PropTypes.string,
  sendMessage: PropTypes.func,
  roomId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  typingIndicator: PropTypes.string,
};

export default MessageTextArea;
